import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Lock, Unlock, ShieldCheck, Calendar, BookOpen, FileText, Wallet,
  TrendingUp, AlertTriangle, CheckCircle2
} from 'lucide-react';

// Phase 7.x · Fix #7 · Financial Period Engine — UI.
// Lists every accounting period for the active tenant, surfaces its
// lock state, and gates Lock / Unlock / Close actions (Close requires
// LOCKED first — defense in depth). Selecting a period drills into a
// summary card with journals/invoices/expenses/payments totals.

const STATE_META = {
  open:   { color: 'emerald', label: 'OPEN',   Icon: Unlock },
  frozen: { color: 'amber',   label: 'LOCKED', Icon: Lock },
  closed: { color: 'rose',    label: 'CLOSED', Icon: ShieldCheck },
};

const Periods = ({ user }) => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const canAct = user?.role === 'admin';

  const fetchPeriods = async () => {
    try {
      const r = await axiosInstance.get('/periods-mgmt');
      setPeriods(r.data?.periods || []);
    } catch (e) {
      toast.error('Failed to load periods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPeriods(); }, []);

  const openSummary = async (p) => {
    setSelected(p);
    setSummary(null);
    try {
      const r = await axiosInstance.get(`/periods-mgmt/${p.name}/summary`);
      setSummary(r.data);
    } catch (e) {
      toast.error('Failed to load period summary');
    }
  };

  const doAction = async (p, verb) => {
    if (!canAct) return;
    const confirmMsg = verb === 'close'
      ? `إغلاق الفترة ${p.name}؟ هذا غير قابل للتراجع (سيلزم Month-End Reversal لإعادة الفتح).`
      : verb === 'lock'
      ? `قفل الفترة ${p.name}؟ لن تُسجَّل قيود جديدة فيها.`
      : `إعادة فتح الفترة ${p.name}؟`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/periods-mgmt/${p.name}/${verb}`);
      toast.success(`Period ${verb}ed: ${p.name}`);
      await fetchPeriods();
      if (selected?.name === p.name) await openSummary({ ...p });
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${verb} period`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="periods-page" className="p-6">
        <p className="text-gray-500">Loading periods…</p>
      </div>
    );
  }

  return (
    <div data-testid="periods-page" className="space-y-6">
      <div className="page-header">
        <h2 className="page-title flex items-center gap-2">
          <Calendar className="w-6 h-6 text-indigo-600" />
          Accounting Periods · الفترات المحاسبية
        </h2>
        <span className="text-sm text-gray-500">
          {periods.length} period(s) · multi-tenant scoped
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Period list ────────────────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-2">
          {periods.length === 0 && (
            <p className="text-sm text-gray-400 italic">
              لا توجد فترات بعد — استخدم زر القفل في أي صفحة لإنشاء الفترة الحالية تلقائياً.
            </p>
          )}
          {periods.map(p => {
            const meta = STATE_META[(p.status || 'open').toLowerCase()] || STATE_META.open;
            const Icon = meta.Icon;
            const active = selected?.name === p.name;
            return (
              <button
                type="button"
                key={p.id || p.name}
                data-testid={`period-row-${p.name}`}
                onClick={() => openSummary(p)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                  active
                    ? `border-${meta.color}-400 bg-${meta.color}-50`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="min-w-0">
                  <p className="font-mono font-semibold text-gray-900">{p.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {p.start_date} → {p.end_date}
                    {typeof p.journal_count === 'number' && ` · ${p.journal_count} journals`}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-${meta.color}-100 text-${meta.color}-700`}
                >
                  <Icon className="w-3 h-3" />
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Summary pane ───────────────────────────────────────────── */}
        <div className="lg:col-span-2">
          {!selected && (
            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
              <AlertTriangle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">اختر فترة من القائمة لعرض تفاصيلها وإجراء عمليات الإقفال.</p>
            </div>
          )}

          {selected && (
            <div
              data-testid={`period-summary-${selected.name}`}
              className="bg-white border border-gray-200 rounded-xl p-5 space-y-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selected.name}</h3>
                  <p className="text-sm text-gray-500">
                    {selected.start_date} → {selected.end_date}
                  </p>
                  {selected.frozen_at && (
                    <p className="text-xs text-amber-700 mt-1">Locked at {selected.frozen_at}</p>
                  )}
                  {selected.closed_at && (
                    <p className="text-xs text-rose-700 mt-1">Closed at {selected.closed_at}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {(selected.status || 'open') === 'open' && (
                    <button
                      data-testid={`lock-period-${selected.name}`}
                      onClick={() => doAction(selected, 'lock')}
                      disabled={!canAct || busy}
                      className="btn-primary text-sm disabled:opacity-50"
                    >
                      <Lock className="w-4 h-4 inline mr-1" />
                      Lock
                    </button>
                  )}
                  {selected.status === 'frozen' && (
                    <>
                      <button
                        data-testid={`unlock-period-${selected.name}`}
                        onClick={() => doAction(selected, 'unlock')}
                        disabled={!canAct || busy}
                        className="btn-secondary text-sm disabled:opacity-50"
                      >
                        <Unlock className="w-4 h-4 inline mr-1" />
                        Unlock
                      </button>
                      <button
                        data-testid={`close-period-${selected.name}`}
                        onClick={() => doAction(selected, 'close')}
                        disabled={!canAct || busy}
                        className="text-sm px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                      >
                        <ShieldCheck className="w-4 h-4 inline mr-1" />
                        Close
                      </button>
                    </>
                  )}
                  {selected.status === 'closed' && (
                    <span className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-rose-50 text-rose-700 text-sm font-semibold border border-rose-200">
                      <CheckCircle2 className="w-4 h-4" />
                      Immutable
                    </span>
                  )}
                </div>
              </div>

              {/* Totals grid */}
              {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
                  {[
                    { key: 'journals', label: 'Journals', Icon: BookOpen, color: 'indigo' },
                    { key: 'invoices', label: 'Invoices', Icon: FileText, color: 'emerald' },
                    { key: 'expenses', label: 'Expenses', Icon: TrendingUp, color: 'rose' },
                    { key: 'payments', label: 'Payments', Icon: Wallet, color: 'amber' },
                  ].map(({ key, label, Icon, color }) => {
                    const t = summary.totals?.[key] || { count: 0, total: 0 };
                    return (
                      <div
                        key={key}
                        data-testid={`period-total-${key}`}
                        className={`p-3 rounded-lg bg-${color}-50 border border-${color}-100`}
                      >
                        <div className={`flex items-center gap-1 text-xs text-${color}-700 mb-1`}>
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </div>
                        <p className="font-bold text-gray-900">{t.count}</p>
                        <p className={`text-xs text-${color}-700`}>
                          {(t.total || 0).toLocaleString()} SAR
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              {!summary && (
                <p className="text-sm text-gray-400">Loading summary…</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Periods;
